import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabase } from '../services/supabase';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [session, setSession] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [profileLoaded, setProfileLoaded] = useState(false);

    const fetchProfile = useCallback(async (userId) => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
                console.error('Error fetching profile:', error);
            }

            setProfile(data);
        } catch (error) {
            console.error('Error fetching profile:', error);
        } finally {
            setProfileLoaded(true);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Check active sessions and sets the user
        supabase.auth.getSession().then(({ data: { session }, error }) => {
            if (error) {
                console.error("Error getting session:", error);
                setProfileLoaded(true);
                setLoading(false);
                return;
            }
            setSession(session);
            setUser(session?.user ?? null);
            if (session?.user) {
                setProfileLoaded(false);
                fetchProfile(session.user.id);
            } else {
                setProfileLoaded(true);
                setLoading(false);
            }
        }).catch(err => {
            console.error("Unexpected error getting session:", err);
            setProfileLoaded(true);
            setLoading(false);
        });

        // Listen for changes on auth state (logged in, signed out, etc.)
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
            setUser(session?.user ?? null);
            if (session?.user) {
                setProfileLoaded(false);
                fetchProfile(session.user.id);
            } else {
                setProfile(null);
                setProfileLoaded(true);
                setLoading(false);
            }
        });

        return () => subscription.unsubscribe();
    }, [fetchProfile]);

    const signIn = useCallback(async (email, password) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });
        if (error) throw new Error(error.message || "Failed to sign in");
        return data;
    }, []);

    const signOut = useCallback(async () => {
        const { error } = await supabase.auth.signOut();
        if (error) throw new Error(error.message || "Failed to sign out");
    }, []);

    const updateProfile = useCallback(async (updates) => {
        if (!user) return;

        try {
            const { data, error } = await supabase
                .from('profiles')
                .upsert({
                    id: user.id,
                    updated_at: new Date(),
                    ...updates,
                })
                .select()
                .single();

            if (error) throw new Error(error.message || "Failed to update profile");
            setProfile(data);
            return data;
        } catch (error) {
            console.error('Error updating profile:', error);
            throw error;
        }
    }, [user]);

    const value = {
        session,
        user,
        profile,
        profileLoaded,
        signIn,
        signOut,
        updateProfile,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
